// Frontend Error System
// Based on backend error structure but adapted for React/browser environment

import { Left } from "fp-ts/lib/Either";
import { Errors } from "io-ts";
import { PathReporter } from "io-ts/PathReporter";

type Jsonable =
    | string
    | number
    | boolean
    | null
    | undefined
    | readonly Jsonable[]
    | { readonly [key: string]: Jsonable };

export interface ErrorOptions {
    cause?: Error;
    context?: Jsonable;
}

export type DecodingIssue = {
    path: string;
    pathParts: string[];
    expected: string;
    actual: unknown;
    message?: string;
};

// Error codes (mirrored from backend + frontend-specific)
export const ERROR_CODE_NETWORK = 3001;
export const ERROR_CODE_UNKNOWN_API_ERROR = 30011;
export const ERROR_CODE_VALIDATION = 3002;
export const ERROR_CODE_AUTHENTICATION_REQUIRED = 30031;
export const ERROR_CODE_AUTHENTICATION_TIMEOUT = 30033;
export const ERROR_CODE_AUTHENTICATION_INIT_FAILED = 30034;
export const ERROR_CODE_FILE_UPLOAD = 3004;
export const ERROR_INPUT_DECODING_FAILED = 5002;
export const ERROR_CODE_MISSING_PROPERTY = 3006;
export const ERROR_CODE_API_URL = 3007;

// Base frontend error class
export class FrontendError extends Error {
    public readonly statusCode: number;
    public readonly errorCode: number;
    public readonly context?: Jsonable;

    constructor(message: string, statusCode: number, errorCode: number, options?: ErrorOptions) {
        super(message, options?.cause ? { cause: options?.cause } : {});
        this.statusCode = statusCode;
        this.errorCode = errorCode;
        this.context = options?.context;

        Object.defineProperty(this, "name", { value: new.target.name });
        Object.setPrototypeOf(this, new.target.prototype);
    }

    // Convert to plain object for logging/debugging
    toJSON() {
        return {
            name: this.name,
            message: this.message,
            statusCode: this.statusCode,
            errorCode: this.errorCode,
            context: this.context,
            stack: this.stack,
        };
    }
}

// API Error - thrown when backend returns an error
export class ApiError extends FrontendError {
    constructor(response: { statusCode: number; errorCode: number; message: string; context?: any }) {
        super(response.message, response.statusCode, response.errorCode, { context: response.context });
    }

    // Factory method for HTTP responses
    static fromResponse(status: number, data: any): ApiError {
        return new ApiError({
            statusCode: status,
            errorCode: data.errorCode || ERROR_CODE_NETWORK,
            message: data.message || `HTTP ${status} Error`,
            context: data.context,
        });
    }
}

// API Error - thrown when backend returns an error
export class ApiUrlError extends FrontendError {
    constructor(message: string, options?: ErrorOptions) {
        super(message, 404, ERROR_CODE_API_URL, options);
    }
}

// Network/Connection errors
export class NetworkError extends FrontendError {
    constructor(message: string = "Network connection failed", options?: ErrorOptions) {
        super(message, 0, ERROR_CODE_NETWORK, options);
    }
}

// Client-side validation errors
export class ValidationError extends FrontendError {
    public readonly field?: string;

    constructor(message: string, field?: string, options?: ErrorOptions) {
        const context = super(message, 400, ERROR_CODE_VALIDATION, options);
        this.field = field;
    }
}

export class MissingPropertyError extends FrontendError {
    public readonly entity: string;
    public readonly property: string;

    constructor(entity: string, property: string, options?: ErrorOptions) {
        const context = super(
            `missing property '${property}' for '${entity}'`,
            400,
            ERROR_CODE_MISSING_PROPERTY,
            options,
        );
        this.entity = entity;
        this.property = property;
    }
}

export class UnprocessableEntityError extends FrontendError {
    constructor(message: string, errorCode: number, options?: ErrorOptions) {
        super(message, 422, errorCode, options);
    }
}

// Authentication errors
export class AuthenticationError extends FrontendError {
    constructor(message: string = "Authentication required", options?: ErrorOptions) {
        super(message, 401, ERROR_CODE_AUTHENTICATION_REQUIRED, options);
    }
}

export class AuthenticationInitError extends FrontendError {
    constructor(options?: ErrorOptions) {
        super("Authentication initialization failed", 401, ERROR_CODE_AUTHENTICATION_INIT_FAILED, options);
    }
}

export class AuthenticationTimeoutError extends FrontendError {
    constructor(options?: ErrorOptions) {
        super("Authentication timeout", 401, ERROR_CODE_AUTHENTICATION_TIMEOUT, options);
    }
}

// File upload errors
export class FileUploadError extends FrontendError {
    constructor(message: string, options?: ErrorOptions) {
        super(message, 400, ERROR_CODE_FILE_UPLOAD, options);
    }
}

export class UnknownError extends FrontendError {
    constructor(message: string, errorCode: number, options?: ErrorOptions) {
        super(message, 400, errorCode, options);
    }
}

// Type guard functions
export function isFrontendError(error: unknown): error is FrontendError {
    return error instanceof FrontendError;
}

export function isApiError(error: unknown): error is ApiError {
    return error instanceof ApiError;
}

export function isValidationError(error: unknown): error is ValidationError {
    return error instanceof ValidationError;
}
